import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const GROUPS = [
  { key: "projects", label: "Proyectos" },
  { key: "areas", label: "Áreas de trabajo" },
  { key: "activities", label: "Actividades" },
  { key: "resources", label: "Recursos" },
  { key: "contacts", label: "Contactos" },
];

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term } });
        setResults(data);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); setResults(null); navigate(path); };
  const total = results ? GROUPS.reduce((n, g) => n + (results[g.key]?.length || 0), 0) : 0;

  return (
    <div ref={boxRef} className="relative w-full max-w-md" data-testid="global-search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Buscar en todo…"
        className="pl-9 pr-9 h-9 rounded-full"
        data-testid="global-search-input"
      />
      {loading ? (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
      ) : q ? (
        <button onClick={() => { setQ(""); setResults(null); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="global-search-clear" aria-label="Limpiar">
          <X className="h-4 w-4" />
        </button>
      ) : null}

      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-popover shadow-xl z-50 p-1.5" data-testid="global-search-results">
          {!results || total === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="global-search-empty">
              {loading ? "Buscando…" : "Sin resultados."}
            </div>
          ) : (
            GROUPS.map((g) => {
              const items = results[g.key] || [];
              if (!items.length) return null;
              return (
                <div key={g.key} className="mb-1">
                  <div className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</div>
                  {items.map((it) => (
                    <button
                      key={g.key + it.id}
                      onClick={() => go(it.path)}
                      data-testid={`global-search-item-${g.key}-${it.id}`}
                      className="w-full text-left rounded-lg px-2.5 py-2 hover:bg-secondary transition-colors"
                    >
                      <div className="text-sm font-medium truncate">{it.title}</div>
                      {it.subtitle && <div className="text-xs text-muted-foreground truncate">{it.subtitle}</div>}
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
